/**
 * "Attach a photo" on the Contact page (sections/contact.liquid).
 *
 * Shopify's contact form only sends text fields, so a photo can't travel with
 * it. Each photo is uploaded from the browser to Cloudinary the moment it is
 * picked — an unsigned upload preset, so no server and no secret in the theme —
 * and its link goes into the form as a hidden field ("Photo 1", "Photo 2"…),
 * which Shopify prints in the email it sends. The link asks Cloudinary for a
 * JPG, so an iPhone's HEIC photo opens in any browser.
 *
 * Send waits while an upload is running. A photo removed mid-upload is
 * cancelled; one that failed stays listed, out of the email, until removed.
 *
 * Without data-cloud-name and data-upload-preset (uploads not set up yet) the
 * picker still lists the photos but uploads nothing, and the email only names
 * them, so a message sent meanwhile still says a photo was meant to come with
 * it.
 */
import { Component } from "@theme/component";

const IMAGE_NAME = /\.(jpe?g|png|gif|webp|avif|heic|heif)$/i;

class ContactAttach extends Component {
  /** @type {{ file: File, row: HTMLLIElement, preview: string, status: string, url?: string, controller?: AbortController }[]} */
  #items = [];

  setup() {
    this.input = this.$("[data-attach-input]");
    this.trigger = this.$("[data-attach-trigger]");
    this.list = this.$("[data-attach-list]");
    this.error = this.$("[data-attach-error]");
    this.fields = this.$("[data-attach-fields]");
    this.rowTemplate = this.$("template[data-attach-row]");
    this.form = this.closest("form");
    this.submit = this.form?.querySelector('[type="submit"]');
    this.maxFiles = Number(this.dataset.maxFiles) || 3;
    this.maxBytes = (Number(this.dataset.maxMb) || 10) * 1024 * 1024;
    this.connected = Boolean(this.dataset.cloudName && this.dataset.uploadPreset);

    this.trigger.addEventListener("click", () => this.input.click());
    this.input.addEventListener("change", () => {
      this.#add([...this.input.files]);
      this.input.value = ""; // so the same photo can be picked again after removing it
    });
    this.form?.addEventListener("submit", (event) => {
      if (this.#uploading) event.preventDefault();
    });
  }

  get #uploading() {
    return this.#items.some((item) => item.status === "uploading");
  }

  /** @param {File[]} files */
  #add(files) {
    this.#showError("");
    for (const file of files) {
      if (this.#items.length >= this.maxFiles) {
        this.#showError(this.dataset.textLimit);
        break;
      }
      if (!file.type.startsWith("image/") && !IMAGE_NAME.test(file.name)) {
        this.#showError(this.dataset.textNotImage);
        continue;
      }
      if (file.size > this.maxBytes) {
        this.#showError(this.dataset.textTooLarge);
        continue;
      }

      const item = { file, preview: URL.createObjectURL(file), status: "" };
      item.row = this.#buildRow(item);
      this.#items.push(item);
      this.list.append(item.row);

      if (this.connected) this.#upload(item);
      else this.#setStatus(item, "offline");
    }
    this.#sync();
  }

  #buildRow(item) {
    const row = document.importNode(this.rowTemplate.content.firstElementChild, true);
    row.querySelector("[data-attach-name]").textContent = item.file.name;

    // Only Safari previews a HEIC; elsewhere the grey box stays until the
    // upload comes back as a JPG.
    const thumb = row.querySelector("[data-attach-thumb]");
    thumb.addEventListener("error", () => (thumb.hidden = true));
    thumb.src = item.preview;

    const remove = row.querySelector("[data-attach-remove]");
    remove.setAttribute("aria-label", `${this.dataset.textRemove} ${item.file.name}`);
    remove.addEventListener("click", () => this.#remove(item));
    return row;
  }

  async #upload(item) {
    this.#setStatus(item, "uploading");
    item.controller = new AbortController();

    const body = new FormData();
    body.append("file", item.file);
    body.append("upload_preset", this.dataset.uploadPreset);

    try {
      const cloud = encodeURIComponent(this.dataset.cloudName);
      const response = await fetch(`https://api.cloudinary.com/v1_1/${cloud}/image/upload`, {
        method: "POST",
        body,
        signal: item.controller.signal,
      });
      if (!response.ok) throw new Error(`Cloudinary upload failed (${response.status})`);

      const { secure_url: url } = await response.json();
      item.url = url.replace(/\.[a-z0-9]+$/i, ".jpg");

      const thumb = item.row.querySelector("[data-attach-thumb]");
      if (thumb.hidden) {
        thumb.hidden = false;
        thumb.src = item.url.replace("/image/upload/", "/image/upload/c_fill,w_80,h_80/");
      }
      this.#setStatus(item, "uploaded");
    } catch (error) {
      if (item.controller.signal.aborted) return;
      console.error(error);
      this.#setStatus(item, "failed");
    }
  }

  #remove(item) {
    item.controller?.abort();
    URL.revokeObjectURL(item.preview);
    item.row.remove();
    this.#items = this.#items.filter((other) => other !== item);
    this.#showError("");
    this.#sync();
    this.trigger.focus();
  }

  #setStatus(item, status) {
    item.status = status;
    const text = {
      uploading: this.dataset.textUploading,
      uploaded: this.dataset.textUploaded,
      failed: this.dataset.textFailed,
      offline: this.dataset.textOffline,
    }[status];

    const label = item.row.querySelector("[data-attach-status]");
    label.textContent = text || "";
    label.classList.toggle("text-[#b00]", status === "failed");
    label.classList.toggle("text-gray-muted", status !== "failed");
    this.#sync();
  }

  /** The hidden fields, the trigger and Send follow the list. */
  #sync() {
    const values = this.#items.flatMap((item) => {
      if (item.status === "uploaded") return [item.url];
      if (item.status === "offline") return [`${item.file.name} (${this.dataset.textOffline})`];
      return [];
    });

    this.fields.replaceChildren(
      ...values.map((value, index) => {
        const field = document.createElement("input");
        field.type = "hidden";
        field.name = `contact[Photo ${index + 1}]`;
        field.value = value;
        return field;
      }),
    );

    this.trigger.hidden = this.#items.length >= this.maxFiles;
    if (this.submit) this.submit.disabled = this.#uploading;
  }

  #showError(text) {
    this.error.textContent = text || "";
    this.error.hidden = !text;
  }
}

customElements.define("contact-attach", ContactAttach);
